import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";
import { pluralize } from "@/shared/text";

/**
 * Whether the context carries no meaningful data
 * (no datasets, no files, no collections, no devices, no device types, no sessions, no table,
 * no visualizer state).
 */
export function isEmptyContext(ctx: ClientViewingContext): boolean {
  return (
    ctx.dataset_ids.length === 0 &&
    ctx.file_ids.length === 0 &&
    ctx.collection_ids.length === 0 &&
    ctx.device_ids.length === 0 &&
    ctx.device_type_ids.length === 0 &&
    ctx.session_ids.length === 0 &&
    ctx.view_ids.length === 0 &&
    (ctx.table_state === undefined || ctx.table_state === null) &&
    (ctx.visualizer_state === undefined || ctx.visualizer_state === null)
  );
}

/**
 * Extract the number of panels from a visualizer state object, or 0
 * if the structure is unexpected.
 */
export function getVisualizerPanelCount(
  state: Record<string, unknown>,
): number {
  if (
    typeof state === "object" &&
    state !== null &&
    "panels" in state &&
    typeof state.panels === "object" &&
    state.panels !== null
  ) {
    return Object.keys(state.panels).length;
  }
  return 0;
}

/**
 * Build a short human-readable summary of a context, e.g.
 * "Context: 2 datasets, 1 file, visualizer" or "Context cleared".
 */
export function buildContextSummary(ctx: ClientViewingContext): string {
  const parts: string[] = [];

  if (ctx.dataset_ids.length > 0) {
    const n = ctx.dataset_ids.length;
    parts.push(`${n} ${pluralize("dataset", n)}`);
  }
  if (ctx.file_ids.length > 0) {
    const n = ctx.file_ids.length;
    parts.push(`${n} ${pluralize("file", n)}`);
  }
  if (ctx.collection_ids.length > 0) {
    const n = ctx.collection_ids.length;
    parts.push(`${n} ${pluralize("collection", n)}`);
  }
  if (ctx.device_ids.length > 0) {
    const n = ctx.device_ids.length;
    parts.push(`${n} ${pluralize("device", n)}`);
  }
  if (ctx.device_type_ids.length > 0) {
    const n = ctx.device_type_ids.length;
    parts.push(`${n} ${pluralize("device type", n)}`);
  }
  if (ctx.session_ids.length > 0) {
    const n = ctx.session_ids.length;
    parts.push(`${n} ${pluralize("session", n)}`);
  }
  // A loaded View stands for its table; without one the table stands alone.
  if (ctx.view_ids.length > 0) {
    const n = ctx.view_ids.length;
    parts.push(`${n} ${pluralize("view", n)}`);
  } else if (ctx.table_state) {
    parts.push(describeTableState(ctx.table_state));
  }
  if (ctx.visualizer_state) {
    parts.push("visualizer");
  }

  if (parts.length === 0) {
    return "Context cleared";
  }

  return `Context: ${parts.join(", ")}`;
}

/**
 * How a resource table with no View loaded reads in the chat: "All datasets"
 * when it shows every row, "Filtered datasets" once any filter narrows it.
 *
 * Named for what the user sees rather than for the table ("datasets table"),
 * which read like the name of a View. A loaded View is labeled by its own name
 * instead, so this is only the no-View case.
 */
export function describeTableState(
  tableState: NonNullable<ClientViewingContext["table_state"]>,
): string {
  const { target, definition } = tableState;
  const filtered =
    definition.roboql !== null ||
    (definition.filters !== null && definition.filters.filters.length > 0);
  return `${filtered ? "Filtered" : "All"} ${target}`;
}
